import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { memberService, postService } from '../../services/api';
import Post from '../../components/Post/Post';
import {
  FiMapPin, FiBriefcase, FiCalendar, FiMail,
  FiPhone, FiMessageSquare
} from 'react-icons/fi';
import { format } from 'date-fns';
import ProfilePhotos from '../../components/ProfilePhotos/ProfilePhotos';
import Relationships from '../../components/Relationships/Relationships';
import ManagedProfileForm from '../../components/ManagedProfileForm/ManagedProfileForm';
import { FiEdit2 } from 'react-icons/fi';
import './Profile.css';

const MemberProfile = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'about');
  const [showEditManaged, setShowEditManaged] = useState(false);

  const isOwnProfile = user?.memberID === parseInt(id);

  useEffect(() => {
    fetchProfile();
    fetchPosts();
  }, [id]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await memberService.getMember(id);
      if (response.success) {
        setProfile(response.data);
      } else {
        setError('Member not found');
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      setLoadingPosts(true);
      const response = await postService.getMemberPosts(id);
      if (response.success) {
        const postData = Array.isArray(response.data)
          ? response.data
          : (response.data.posts || response.data.items || []);
        setPosts(postData);
      }
    } catch (err) {
      console.error('Failed to fetch posts:', err);
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="loading-state">
            <span className="spinner spinner-lg"></span>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="error-state card">
            <p>{error || 'Member not found'}</p>
            <Link to="/family" className="btn btn-primary">Back to Family</Link>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to own profile page if viewing self
  if (isOwnProfile) {
    window.location.href = '/profile';
    return null;
  }

  return (
    <div className="profile-page">
      {/* Cover Photo */}
      <div className="profile-cover">
        {profile?.coverPhotoURL ? (
          <img src={profile.coverPhotoURL} alt="Cover" className="cover-image" />
        ) : (
          <div className="cover-placeholder"></div>
        )}
      </div>

      <div className="container">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-avatar-section">
            <div className="profile-avatar">
              {profile?.profilePhotoURL ? (
                <img src={profile.profilePhotoURL} alt={profile.fullName} />
              ) : (
                <div className="avatar-placeholder">
                  {getInitials(profile?.firstName, profile?.lastName)}
                </div>
              )}
            </div>
          </div>

          <div className="profile-info">
            <h1 className="profile-name">
              {profile?.fullName}
              {profile?.nickname && <span className="profile-nickname">"{profile.nickname}"</span>}
            </h1>

            {profile?.bio && (
              <p className="profile-bio">{profile.bio}</p>
            )}

            <div className="profile-details">
              {profile?.currentCity && (
                <span className="profile-detail">
                  <FiMapPin /> {profile.currentCity}
                </span>
              )}
              {profile?.occupation && (
                <span className="profile-detail">
                  <FiBriefcase /> {profile.occupation}
                </span>
              )}
              {profile?.dateOfBirth && (
                <span className="profile-detail">
                  <FiCalendar /> {format(new Date(profile.dateOfBirth.split('T')[0] + 'T12:00:00'), 'MMMM d')}
                </span>
              )}
            </div>
          </div>

          <div className="profile-actions">
            {profile?.isManagedProfile && (profile?.managedByMemberID === user?.memberID || user?.isAdmin) ? (
              <button className="btn btn-primary" onClick={() => setShowEditManaged(true)}>
                <FiEdit2 /> Edit Profile
              </button>
            ) : (
              <Link to={`/messages?to=${profile.memberID}`} className="btn btn-primary">
                <FiMessageSquare /> Message
              </Link>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="profile-tabs">
          {!profile?.isManagedProfile && (
            <button
              className={`profile-tab ${activeTab === 'posts' ? 'active' : ''}`}
              onClick={() => setActiveTab('posts')}
            >
              Posts
            </button>
          )}
          <button
            className={`profile-tab ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
          <button
            className={`profile-tab ${activeTab === 'photos' ? 'active' : ''}`}
            onClick={() => setActiveTab('photos')}
          >
            Photos
          </button>
          <button
            className={`profile-tab ${activeTab === 'relationships' ? 'active' : ''}`}
            onClick={() => setActiveTab('relationships')}
          >
            Relationships
          </button>
        </div>

        {/* Tab Content */}
        <div className="profile-content">
          {activeTab === 'posts' && (
            <div className="profile-posts">
              {loadingPosts ? (
                <div className="loading-state">
                  <span className="spinner"></span>
                  <p>Loading posts...</p>
                </div>
              ) : posts.length === 0 ? (
                <div className="empty-state card">
                  <div className="empty-state-icon">📝</div>
                  <h3 className="empty-state-title">No posts yet</h3>
                  <p>{profile.firstName} hasn't shared any posts yet.</p>
                </div>
              ) : (
                posts.map(post => (
                  <Post
                    key={post.postID}
                    post={post}
                  />
                ))
              )}
            </div>
          )}

          {activeTab === 'about' && (
            <div className="profile-about card">
              <div className="card-body">
                <h3>About {profile?.firstName}</h3>

                <div className="about-section">
                  {profile?.bio && (
                    <div className="about-item">
                      <h4>Bio</h4>
                      <p>{profile.bio}</p>
                    </div>
                  )}

                  <div className="about-grid">
                    {profile?.email && !profile?.isManagedProfile && (
                      <div className="about-item">
                        <FiMail className="about-icon" />
                        <div>
                          <h4>Email</h4>
                          <p><a href={`mailto:${profile.email}`}>{profile.email}</a></p>
                        </div>
                      </div>
                    )}

                    {profile?.phoneNumber && !profile?.isManagedProfile && (
                      <div className="about-item">
                        <FiPhone className="about-icon" />
                        <div>
                          <h4>Phone</h4>
                          <p><a href={`tel:${profile.phoneNumber}`}>{profile.phoneNumber}</a></p>
                        </div>
                      </div>
                    )}

                    {profile?.currentCity && (
                      <div className="about-item">
                        <FiMapPin className="about-icon" />
                        <div>
                          <h4>Current City</h4>
                          <p>{profile.currentCity}</p>
                        </div>
                      </div>
                    )}

                    {profile?.hometown && (
                      <div className="about-item">
                        <FiMapPin className="about-icon" />
                        <div>
                          <h4>Hometown</h4>
                          <p>{profile.hometown}</p>
                        </div>
                      </div>
                    )}

                    {profile?.occupation && (
                      <div className="about-item">
                        <FiBriefcase className="about-icon" />
                        <div>
                          <h4>Occupation</h4>
                          <p>{profile.occupation}</p>
                        </div>
                      </div>
                    )}

                    {profile?.dateOfBirth && (
                      <div className="about-item">
                        <FiCalendar className="about-icon" />
                        <div>
                          <h4>{profile?.isDeceased ? 'Born' : 'Birthday'}</h4>
                          <p>{format(new Date(profile.dateOfBirth.split('T')[0] + 'T12:00:00'), profile?.isDeceased ? 'MMMM d, yyyy' : 'MMMM d')}</p>
                        </div>
                      </div>
                    )}

                    {profile?.isDeceased && profile?.deceasedDate && (
                      <div className="about-item">
                        <FiCalendar className="about-icon" />
                        <div>
                          <h4>Passed Away</h4>
                          <p>{format(new Date(profile.deceasedDate.split('T')[0] + 'T12:00:00'), 'MMMM d, yyyy')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'photos' && (
            <div className="profile-photos card">
              <div className="card-body">
                <ProfilePhotos memberId={parseInt(id)} isOwnProfile={false} />
              </div>
            </div>
          )}

          {activeTab === 'relationships' && (
            <div className="profile-relationships card">
              <div className="card-body">
                <Relationships 
                  memberId={parseInt(id)} 
                  isOwnProfile={true} 
                  canManage={
                    !profile?.isManagedProfile ||
                    profile?.managedByMemberID === user?.memberID ||
                    user?.isAdmin
                  }
                  isManagingProfile={
                    profile?.isManagedProfile && 
                    (profile?.managedByMemberID === user?.memberID || user?.isAdmin)
                  }
                />
              </div>
            </div>
          )}

        </div>
      </div>

      {showEditManaged && (
        <ManagedProfileForm
          existingProfile={profile}
          onClose={() => setShowEditManaged(false)}
          onUpdated={(updated) => setProfile(updated)}
        />
      )}
    </div>
  );
};

export default MemberProfile;